import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Grid,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { getAopPlans, deleteAopPlan } from "../../Services/Brokerage";
import CreateAopWizard from "./CreateAopWizard";
import { supabase } from "../../supabaseClient";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const AOPConfigurations = () => {
  const [aopConfigs, setAopConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedAopId, setSelectedAopId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAop, setSelectedAop] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aopToDelete, setAopToDelete] = useState(null);

  const [openProjectsDialog, setOpenProjectsDialog] = useState(false);
  const [includedProjects, setIncludedProjects] = useState([]);

  // ✅ NEW STATES FOR CPs
  const [openCpDialog, setOpenCpDialog] = useState(false);
  const [selectedCps, setSelectedCps] = useState([]);

  useEffect(() => {
    fetchAops();
  }, []);

  const fetchAops = async () => {
    setLoading(true);
    try {
      const data = await getAopPlans();
      setAopConfigs(data || []);
    } catch (err) {
      console.error("Failed to fetch AOPs", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAop = (id) => {
    setAopToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAop = async () => {
    if (!aopToDelete) return;

    const res = await deleteAopPlan(aopToDelete);

    if (res.success) {
      fetchAops();
    } else {
      alert(res.message || "Delete failed");
    }

    setDeleteDialogOpen(false);
    setAopToDelete(null);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAopToDelete(null);
  };

  const handleViewAop = async (aopId) => {
    setSelectedAopId(aopId);

    const { data, error } = await supabase
      .from("aop_plans")
      .select(
        `
        *,
        aop_slabs (*),
        aop_eligibility_rules (*),
        aop_scope_projects(
          project_id,
          Projects(
            projectName
          )
        ),
        aop_cp_targets(
          cp_id,
          CP(
            cpId,
            billingName
          )
        )
      `,
      )
      .eq("id", aopId)
      .single();

    if (!error) {
      setSelectedAop(data);

      // ✅ SET SELECTED CPs
      const cpList =
        data?.aop_cp_targets?.map((item) => ({
          cpId: item?.CP?.cpId || item?.cp_id,
          billingName: item?.CP?.billingName || "-",
        })) || [];

      setSelectedCps(cpList);

      setOpenDialog(true);
    } else {
      console.error("Error fetching AOP details:", error);
    }
  };

  const formatIndian = (value) => {
    if (value === null || value === undefined) return "∞";
    return Number(value).toLocaleString("en-IN");
  };

  useEffect(() => {
    const fetchIncludedProjects = async () => {
      if (!selectedAop) return;

      try {
        const { data: allProjects, error } = await supabase
          .from("Projects")
          .select("projectId, projectName")
          .eq("isVJ", true);

        if (error) {
          console.error("Error fetching projects:", error);
          return;
        }

        const excludedIds =
          selectedAop.aop_scope_projects?.map((p) => p.project_id) || [];

        const included = (allProjects || []).filter(
          (p) => !excludedIds.includes(p.projectId),
        );

        setIncludedProjects(included);
      } catch (err) {
        console.error("Error:", err);
      }
    };

    fetchIncludedProjects();
  }, [selectedAop]);

  return (
    <Box sx={{ p: 1, minHeight: "100vh" }}>
      {!showWizard && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography fontSize={16} fontWeight={600}>
            Active AOP Configurations
          </Typography>

          <Button
            variant="contained"
            sx={{ textTransform: "none", borderRadius: 2 }}
            onClick={() => setShowWizard(true)}
          >
            + Create New AOP
          </Button>
        </Box>
      )}

      {showWizard ? (
        <CreateAopWizard
          onCancel={() => setShowWizard(false)}
          onSuccess={() => {
            setShowWizard(false);
            fetchAops();
          }}
        />
      ) : (
        <>
          {loading ? (
            <Typography>Loading AOPs...</Typography>
          ) : aopConfigs.length === 0 ? (
            <Typography color="text.secondary">
              No AOPs configured yet
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {aopConfigs.map((config) => (
                <Box
                  key={config.id}
                  sx={{
                    mb: 1,
                    p: 3,
                    borderRadius: 3,
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: "#E8F0FE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DescriptionOutlinedIcon sx={{ color: "#3B82F6" }} />
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight={600}>
                          {config.year}
                        </Typography>

                        <Chip
                          label={config.status}
                          size="small"
                          sx={{
                            backgroundColor:
                              config.status === "Active"
                                ? "#E6F4EA"
                                : "#FDECEA",
                            color:
                              config.status === "Active"
                                ? "#1E7E34"
                                : "#B42318",
                            fontWeight: 500,
                            borderRadius: 2,
                          }}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {config.slabs} slab{config.slabs !== 1 ? "s" : ""}{" "}
                        configured
                      </Typography>
                    </Grid>

                    <Grid item xs={4}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Validity:{" "}
                          {config.validity_start_date
                            ? new Date(
                                config.validity_start_date,
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "-"}{" "}
                          -{" "}
                          {config.validity_end_date
                            ? new Date(
                                config.validity_end_date,
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "-"}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={2}>
                          <IconButton
                            size="small"
                            sx={{ borderRadius: 2 }}
                            onClick={() => handleViewAop(config.id)}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 22 }} />
                          </IconButton>

                          {config.canDelete && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAop(config.id)}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      {/* VIEW AOP DIALOG */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>AOP Configuration</DialogTitle>

        <DialogContent dividers>
          {selectedAop && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* PLAN INFO */}
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Typography>
                  <b>Financial Year:</b> {selectedAop.financial_year}
                </Typography>

                <Typography>
                  <b>Status:</b> {selectedAop.status}
                </Typography>

                <Typography>
                  <b>Validity Start:</b>{" "}
                  {selectedAop.validity_start_date
                    ? new Date(
                        selectedAop.validity_start_date,
                      ).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </Typography>

                <Typography>
                  <b>Validity End:</b>{" "}
                  {selectedAop.validity_end_date
                    ? new Date(
                        selectedAop.validity_end_date,
                      ).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </Typography>

                <Typography>
                  <b>Calculation Basis:</b> {selectedAop.calculation_basis}
                </Typography>

                <Typography>
                  <b>Reward Type:</b> {selectedAop.reward_type}
                </Typography>
              </Box>

              {/* ✅ CP SCOPE */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={600}>
                  CP Scope:{" "}
                  {selectedAop.cp_scope_type === "ALL"
                    ? "All CPs"
                    : `Specific CPs (${selectedCps.length})`}
                </Typography>

                {selectedAop.cp_scope_type === "SPECIFIC" && (
                  <IconButton
                    size="small"
                    onClick={() => setOpenCpDialog(true)}
                    sx={{
                      color: "#2563EB",
                      width: 30,
                      height: 30,
                      backgroundColor: "#EEF4FF",
                      "&:hover": {
                        backgroundColor: "#DBEAFE",
                      },
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>

              {/* INCLUDED PROJECTS */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={600}>
                  Included Projects ({includedProjects.length})
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => setOpenProjectsDialog(true)}
                  sx={{
                    color: "#2563EB",
                    width: 30,
                    height: 30,
                    backgroundColor: "#EEF4FF",
                    "&:hover": {
                      backgroundColor: "#DBEAFE",
                    },
                  }}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* SLABS TABLE */}
              <Box>
                <Typography fontWeight={600} mb={1}>
                  Incentive Slabs
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table
                    sx={{
                      "& .MuiTableCell-root": {
                        fontSize: "16px",
                        padding: "14px 16px",
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        fontWeight: 600,
                        fontSize: "17px",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Incentive</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {selectedAop.aop_slabs?.map((slab) => (
                        <TableRow key={slab.id}>
                          <TableCell>
                            {selectedAop.calculation_basis === "VALUE"
                              ? `₹ ${formatIndian(slab.from_value)}`
                              : formatIndian(slab.from_value)}
                          </TableCell>

                          <TableCell>
                            {slab.to_value
                              ? selectedAop.calculation_basis === "VALUE"
                                ? `₹ ${formatIndian(slab.to_value)}`
                                : formatIndian(slab.to_value)
                              : "∞"}
                          </TableCell>

                          <TableCell>
                            {selectedAop.reward_type === "PERCENTAGE"
                              ? `${slab.percentage}%`
                              : `₹ ${formatIndian(slab.amount)}`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* ELIGIBILITY */}
              <Box>
                <Typography fontWeight={600} mb={1}>
                  Eligibility
                </Typography>

                {selectedAop.aop_eligibility_rules?.map((rule) => (
                  <Box
                    key={rule.id}
                    display="grid"
                    gridTemplateColumns="1fr 1fr"
                    gap={2}
                    mb={1}
                  >
                    <Typography>
                      <b>Site Visits:</b> {rule.min_site_visits || 0} -{" "}
                      {rule.max_site_visits || "∞"}
                    </Typography>

                    <Typography>
                      <b>FOS Count:</b> {rule.min_fos_count || 0} -{" "}
                      {rule.max_fos_count || "∞"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete AOP Configuration
        </DialogTitle>

        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete this AOP configuration?
          </Typography>

          <Typography color="error" mt={1}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteAop}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* INCLUDED PROJECTS DIALOG */}
      <Dialog
        open={openProjectsDialog}
        onClose={() => setOpenProjectsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Included Projects</DialogTitle>

        <DialogContent dividers>
          {includedProjects.length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {includedProjects.map((p) => (
                <Chip
                  key={p.projectId}
                  label={p.projectName}
                  sx={{
                    backgroundColor: "#EEF4FF",
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No included projects
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenProjectsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ SELECTED CPs DIALOG */}
      <Dialog
        open={openCpDialog}
        onClose={() => setOpenCpDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Selected Channel Partners</DialogTitle>

        <DialogContent dividers>
          {selectedAop?.cp_scope_type === "ALL" ? (
            <Typography color="text.secondary">All CPs are included</Typography>
          ) : selectedCps.length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {selectedCps.map((cp) => (
                <Chip
                  key={cp.cpId}
                  label={cp.billingName}
                  sx={{
                    backgroundColor: "#EEF4FF",
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">No CPs found</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenCpDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AOPConfigurations;